import { addWorkingMinutes, type OfficeSchedule } from './officeHours.ts';

/**
 * Domain vocabulary shared by the server and the web app. Edit the
 * organisation-specific lists (departments, offices, categories) here only.
 */

export const DEPARTMENTS = [
  'Main',
  'Justice, Peace & Humanitarian Response',
  'Livelihood & Agric',
  'Finance',
  'Education',
  'Health',
  'M&E',
  'Others',
] as const;

export const OFFICE_LOCATIONS = ['Bishop Secretariat', 'FTC', 'Others'] as const;

export const CATEGORIES = [
  'Internet',
  'Printer',
  'Desktop',
  'Laptop',
  'Email',
  'Software',
  'Hardware',
  'Other',
] as const;
export type Category = (typeof CATEGORIES)[number];

export const CATEGORY_LABELS: Record<Category, string> = {
  Internet: 'Internet / Network',
  Printer: 'Printer',
  Desktop: 'Desktop / PC',
  Laptop: 'Laptop',
  Email: 'Email',
  Software: 'Software / Application',
  Hardware: 'Other Hardware',
  Other: 'Something else',
};

export const PRIORITIES = ['Low', 'Medium', 'High', 'Critical'] as const;
export type Priority = (typeof PRIORITIES)[number];

export const SERVICE_MODES = ['it_visits', 'user_visits', 'remote'] as const;
export type ServiceMode = (typeof SERVICE_MODES)[number];

export const SERVICE_MODE_LABELS: Record<ServiceMode, string> = {
  it_visits: 'Please come to my office',
  user_visits: 'I will come to the IT office',
  remote: 'Help me remotely (phone call or chat)',
};

export const ROLES = ['staff', 'technician', 'admin'] as const;
export type Role = (typeof ROLES)[number];

export const IT_ROLES: readonly Role[] = ['technician', 'admin'];
export const isItRole = (role: Role): boolean => IT_ROLES.includes(role);

// ── Ticket lifecycle ────────────────────────────────────────────────

/**
 * `void` is set only by the separate admin "void" action (spam, duplicates,
 * tests, mistakes): the ticket keeps its number and history but leaves every
 * queue, figure and report. It can be restored.
 */
export const STATUSES = ['open', 'in_progress', 'waiting', 'resolved', 'closed', 'void'] as const;
export type Status = (typeof STATUSES)[number];

export const STATUS_LABELS: Record<Status, string> = {
  open: 'Open',
  in_progress: 'In progress',
  waiting: 'Waiting on requester',
  resolved: 'Resolved',
  closed: 'Closed',
  void: 'Voided',
};

/** Statuses of tickets that are still open (not yet resolved or closed). */
export const ACTIVE_STATUSES: readonly Status[] = ['open', 'in_progress', 'waiting'];

/**
 * Statuses in which the response deadline (SLA) clock runs. It pauses while
 * a ticket is waiting on the requester: that delay isn't IT's, and the time
 * left carries over when they reply.
 */
export const SLA_RUNNING_STATUSES: readonly Status[] = ['open', 'in_progress'];

/**
 * Status changes an IT user may make directly. Moving to `resolved` also
 * requires resolution notes; `closed` is final.
 */
export const STATUS_TRANSITIONS: Record<Status, readonly Status[]> = {
  open: ['in_progress', 'waiting', 'resolved', 'closed'],
  in_progress: ['waiting', 'resolved', 'closed'],
  waiting: ['in_progress', 'resolved', 'closed'],
  resolved: ['in_progress', 'closed'],
  closed: [],
  // Leaving `void` is the separate "restore" action.
  void: [],
};

export function canTransition(from: Status, to: Status): boolean {
  return STATUS_TRANSITIONS[from].includes(to);
}

// ── SLA ─────────────────────────────────────────────────────────────

/**
 * Time to resolution before a ticket counts as overdue, in working minutes
 * (office hours only; see officeHours.ts). A working day is 8 hours
 * (08:00–17:00 less the lunch break).
 */
export const SLA_MINUTES: Record<Priority, number> = {
  Critical: 60,
  High: 4 * 60,
  Medium: 8 * 60,
  Low: 3 * 8 * 60,
};

/** Wording shown to requesters after they submit. */
export const RESPONSE_ESTIMATES: Record<Priority, string> = {
  Critical: 'within 1 hour',
  High: 'within 4 hours',
  Medium: 'within 1 business day',
  Low: 'within 3 business days',
};

/** The deadline, counting office hours only. `extraMinutes`: working time spent waiting on the requester. */
export function slaDueAt(
  createdAt: Date,
  priority: Priority,
  schedule: OfficeSchedule,
  extraMinutes = 0,
): Date {
  return addWorkingMinutes(schedule, createdAt, SLA_MINUTES[priority] + extraMinutes);
}

export function isOverdue(
  ticket: { status: Status; dueAt: Date | string | null },
  now: Date = new Date(),
): boolean {
  if (!SLA_RUNNING_STATUSES.includes(ticket.status) || !ticket.dueAt) return false;
  return new Date(ticket.dueAt).getTime() < now.getTime();
}

// ── Ticket numbers ──────────────────────────────────────────────────

export const TICKET_NUMBER_PATTERN = /^NAB-\d{4}-\d{4,}$/;

export function formatTicketNumber(year: number, sequence: number): string {
  return `NAB-${year}-${String(sequence).padStart(4, '0')}`;
}
